import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { CONTENT_LANGS, CONTENT_LANG_LABELS } from './content-langs'

interface I18nFieldsSectionProps {
  value: {
    nameI18n: Record<string, string>
    descriptionI18n: Record<string, string>
  }
  onChange: (field: 'nameI18n' | 'descriptionI18n', lang: string, text: string) => void
  withDescription?: boolean
}

/**
 * 어드민 폼 공용 번역 입력 섹션 (접이식).
 * 빈 입력은 그대로 제출해도 백엔드에서 제거되므로 별도 정리가 필요 없다.
 */
export function I18nFieldsSection({ value, onChange, withDescription = false }: I18nFieldsSectionProps) {
  return (
    <details className="rounded-lg border border-gray-200">
      <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium text-gray-700">
        번역 (선택)
      </summary>
      <div className="space-y-4 border-t border-gray-200 px-4 py-4">
        <p className="text-xs text-gray-500">
          비워두면 해당 언어 사용자에게 한국어 원문이 표시됩니다.
        </p>
        {CONTENT_LANGS.map((lang) => (
          <div key={lang} className="space-y-2">
            <Label>{CONTENT_LANG_LABELS[lang]}</Label>
            <Input
              value={value.nameI18n[lang] ?? ''}
              onChange={(e) => onChange('nameI18n', lang, e.target.value)}
              placeholder="이름 번역"
            />
            {withDescription && (
              <Textarea
                value={value.descriptionI18n[lang] ?? ''}
                onChange={(e) => onChange('descriptionI18n', lang, e.target.value)}
                placeholder="설명 번역"
                rows={2}
              />
            )}
          </div>
        ))}
      </div>
    </details>
  )
}

interface I18nPromptFieldsSectionProps {
  value: Record<string, string>
  onChange: (lang: string, text: string) => void
}

/**
 * 템플릿 프롬프트(textPrompt) 전용 언어별 번역 입력 섹션 (접이식).
 * ${KEY:-기본값} 파라미터의 KEY 는 모든 언어에서 동일해야 하므로, 기본값과 주변 텍스트만 번역한다.
 */
export function I18nPromptFieldsSection({ value, onChange }: I18nPromptFieldsSectionProps) {
  return (
    <details className="rounded-lg border border-gray-200">
      <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium text-gray-700">
        프롬프트 번역 (선택)
      </summary>
      <div className="space-y-4 border-t border-gray-200 px-4 py-4">
        <p className="text-xs text-gray-500">
          비워두면 해당 언어 사용자에게 한국어 원문 프롬프트가 표시됩니다.{' '}
          <code className="rounded bg-gray-100 px-1 py-0.5">{'${KEY:-기본값}'}</code> 형식의
          파라미터에서 <strong>KEY 는 모든 언어에서 동일하게</strong> 두고 기본값·주변 텍스트만 번역하세요.
        </p>
        {CONTENT_LANGS.map((lang) => (
          <div key={lang} className="space-y-2">
            <Label>{CONTENT_LANG_LABELS[lang]}</Label>
            <Textarea
              value={value[lang] ?? ''}
              onChange={(e) => onChange(lang, e.target.value)}
              placeholder="프롬프트 번역"
              rows={4}
            />
          </div>
        ))}
      </div>
    </details>
  )
}

interface I18nTranslationsCardProps {
  nameI18n?: Record<string, string> | null
  descriptionI18n?: Record<string, string> | null
}

/**
 * 상세 페이지용 읽기 전용 번역 카드. 입력된 번역이 하나도 없으면 렌더링하지 않는다.
 */
export function I18nTranslationsCard({ nameI18n, descriptionI18n }: I18nTranslationsCardProps) {
  const langs = CONTENT_LANGS.filter((lang) => nameI18n?.[lang] || descriptionI18n?.[lang])
  if (langs.length === 0) return null

  return (
    <div className="bg-white rounded-lg border p-6">
      <h2 className="text-lg font-semibold text-heading mb-4 tracking-tight">번역</h2>
      <div className="space-y-3">
        {langs.map((lang) => (
          <div key={lang} className="flex items-baseline gap-4">
            <span className="w-24 flex-shrink-0 text-sm font-medium text-gray-700">
              {CONTENT_LANG_LABELS[lang]}
            </span>
            <div className="min-w-0">
              <div className="text-gray-900">{nameI18n?.[lang] || '-'}</div>
              {descriptionI18n?.[lang] && (
                <div className="text-sm text-gray-500">{descriptionI18n[lang]}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
